/**
 * Task Routing Configuration UI
 * Manage task-to-model mappings with drag-and-drop reordering
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';

interface STTConfig {
  model?: string;
  language?: string;
  smart_format?: boolean;
  endpointing?: number;
  encoding?: string;
  sample_rate?: number;
  interim_results?: boolean;
}

interface TaskRoutingConfig {
  _version: number;
  quality_score: number;
  primary_models: string[];
  fallback_models: string[];
  reason: string;
  stt_config?: STTConfig;
}

export class TaskRoutingUI extends BaseComponent {
  private apiClient: APIClient;
  private tasks: Record<string, TaskRoutingConfig> = {};
  private taskSources: Record<string, 'default' | 'kv'> = {};
  private allModels: string[] = [];
  private loading = false;
  private editingTask: string | null = null;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="routing-config-ui">
        <div class="routing-header">
          <h2>📋 Task Routing Configuration</h2>
          <p class="subtitle">Map tasks to AI models (drag to reorder priority)</p>
        </div>

        <div class="routing-toolbar">
          <button id="refresh-btn" class="btn btn-secondary">
            <span>🔄</span> Refresh
          </button>
        </div>

        <div id="tasks-container" class="tasks-container">
          <div class="loading">Loading task routing...</div>
        </div>

        <div id="task-edit-modal" class="modal" style="display: none;">
          <div class="modal-content">
            <div class="modal-header">
              <h3 id="modal-title">Edit Task Routing</h3>
              <button id="modal-close" class="close-btn">&times;</button>
            </div>
            <div id="modal-body" class="modal-body">
              <!-- Form will be injected -->
            </div>
          </div>
        </div>
      </div>
    `;

    document.getElementById('refresh-btn')?.addEventListener('click', async () => {
      await this.loadModels();
      await this.loadTasks();
    });
    document.getElementById('modal-close')?.addEventListener('click', () => this.hideModal());
    await this.loadModels();
    await this.loadTasks();
  }

  private async loadModels(): Promise<void> {
    try {
      const response = await this.apiClient.get<{ models: Record<string, any> }>('/v1/admin/config/models');
      this.allModels = Object.keys(response.data.models || {});
    } catch (error) {
      console.error('Failed to load models:', error);
      this.allModels = [];
    }
  }

  private async loadTasks(): Promise<void> {
    this.loading = true;
    const container = document.getElementById('tasks-container');
    if (!container) return;

    container.innerHTML = '<div class="loading">⏳ Loading...</div>';

    try {
      const response = await this.apiClient.get<{ tasks: Record<string, TaskRoutingConfig>; sources?: Record<string, 'default' | 'kv'> }>('/v1/admin/config/task-routing');
      this.tasks = response.data.tasks || {};
      this.taskSources = response.data.sources || {};
      this.renderTasks();
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to load task routing</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    } finally {
      this.loading = false;
    }
  }

  private renderTasks(): void {
    const container = document.getElementById('tasks-container');
    if (!container) return;

    const taskEntries = Object.entries(this.tasks);
    if (taskEntries.length === 0) {
      container.innerHTML = '<div class="no-results">No task routing configured</div>';
      return;
    }

    container.innerHTML = `
      <div class="tasks-grid">
        ${taskEntries.map(([task, config]) => this.renderTaskCard(task, config)).join('')}
      </div>
    `;

    // Attach event listeners
    taskEntries.forEach(([task]) => {
      document.getElementById(`edit-${task}`)?.addEventListener('click', () => this.showEditModal(task));
      document.getElementById(`delete-${task}`)?.addEventListener('click', () => this.deleteTask(task));
    });
  }

  private renderTaskCard(task: string, config: TaskRoutingConfig): string {
    const taskName = task.replace('consenger/', '');
    const isOverride = this.taskSources[task] === 'kv';

    return `
      <div class="task-card ${isOverride ? 'task-override' : ''}">
        <div class="task-header">
          <div>
            <h4>${taskName}${isOverride ? ' <span class="override-badge">📌 KV Override</span>' : ''}</h4>
            <span class="quality-badge">Quality Score: ${(config.quality_score * 10).toFixed(1)}/10</span>
          </div>
          <div class="model-actions">
            <button id="edit-${task}" class="icon-btn" title="Edit">✏️</button>
            <button id="delete-${task}" class="icon-btn" title="Reset to default">🔄</button>
          </div>
        </div>
        <p class="task-description">${config.reason}</p>
        
        ${config.stt_config ? `
        <div class="stt-config-summary">
          <strong>🎙️ STT Config:</strong>
          <ul>
            <li>Lang: <code>${config.stt_config.language || 'default'}</code></li>
            <li>Smart Format: <code>${config.stt_config.smart_format ?? 'default'}</code></li>
            <li>Endpointing: <code>${config.stt_config.endpointing ?? 'default'}</code>ms</li>
          </ul>
        </div>
        ` : ''}

        <div class="task-models">
          <strong>Primary Models (${config.primary_models?.length || 0}):</strong>
          <ol class="model-priority-list">
            ${(config.primary_models || []).map((m, i) => `<li><code>${m}</code> ${i === 0 ? '⭐ Primary' : ''}</li>`).join('')}
          </ol>
          <strong>Fallback Models (${config.fallback_models?.length || 0}):</strong>
          <ol class="model-priority-list">
            ${(config.fallback_models || []).map(m => `<li><code>${m}</code> 🔄</li>`).join('')}
          </ol>
        </div>
      </div>
    `;
  }

  private showEditModal(task: string): void {
    this.editingTask = task;
    const config = this.tasks[task];
    if (!config) return;

    const modal = document.getElementById('task-edit-modal');
    const modalTitle = document.getElementById('modal-title');
    const modalBody = document.getElementById('modal-body');

    if (!modal || !modalTitle || !modalBody) return;

    const taskName = task.replace('consenger/', '');
    modalTitle.textContent = `Edit Task: ${taskName}`;

    const primaryModels = config.primary_models || [];
    const fallbackModels = config.fallback_models || [];
    const allSelected = [...primaryModels, ...fallbackModels];
    const availableModels = this.allModels.filter((m: string) => !allSelected.includes(m));

    modalBody.innerHTML = `
      <div class="task-edit-form">
        <div class="form-section">
          <h4>Primary Models (High Priority)</h4>
          <p class="help-text">Drag to reorder</p>
          <div id="primary-models" class="model-list selected-models primary">
            ${primaryModels.map((m: string, i: number) => `
              <div class="model-item" data-model="${m}" draggable="true">
                <span class="drag-handle">☰</span>
                <span class="model-priority">P${i + 1}</span>
                <code>${m}</code>
                <button class="remove-model" data-model="${m}" data-list="primary">❌</button>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="form-section">
          <h4>Fallback Models (Secondary)</h4>
          <p class="help-text">Drag to reorder</p>
          <div id="fallback-models" class="model-list selected-models fallback">
            ${fallbackModels.map((m: string, i: number) => `
              <div class="model-item" data-model="${m}" draggable="true">
                <span class="drag-handle">☰</span>
                <span class="model-priority">F${i + 1}</span>
                <code>${m}</code>
                <button class="remove-model" data-model="${m}" data-list="fallback">❌</button>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="form-section">
          <h4>Available Models</h4>
          <div id="available-models" class="model-list available-models">
            ${availableModels.length === 0 ? '<p class="no-results">All models selected</p>' :
        availableModels.map(m => `
                <div class="model-item available" data-model="${m}">
                  <code>${m}</code>
                  <div class="add-buttons">
                    <button class="add-model" data-model="${m}" data-list="primary">➕ Primary</button>
                    <button class="add-model" data-model="${m}" data-list="fallback">➕ Fallback</button>
                  </div>
                </div>
              `).join('')
      }
          </div>
        </div>

        <div class="form-section">
          <div class="form-group">
            <label>Quality Score (0-1, shown as /10)</label>
            <input type="number" id="quality-score" value="${config.quality_score}" min="0" max="1" step="0.1" />
          </div>
          <div class="form-group">
            <label>Reason / Annotation</label>
            <input type="text" id="description" value="${config.reason}" />
          </div>
        </div>

        <div class="form-section">
          <h4>🎙️ STT Configuration (Optional)</h4>
          <p class="help-text warning">
            ⚠️ <strong>Warning:</strong> These settings will <strong>override</strong> agent defaults if configured.<br>
            Leave empty to use agent's dynamic parameters (recommended for language).
          </p>
          <div class="form-grid">
            <div class="form-group">
              <label>
                Language
                <span class="param-tag dynamic">Dynamic</span>
              </label>
              <input
                type="text"
                id="stt-language"
                value="${config.stt_config?.language || ''}"
                placeholder="Leave empty (recommended)"
              />
              <p class="field-help">
                ⚠️ Users can change language in iOS app. Leave empty to allow dynamic switching.
              </p>
            </div>
            <div class="form-group">
              <label>
                Smart Format
                <span class="param-tag static">Static</span>
              </label>
              <select id="stt-smart-format">
                <option value="" ${config.stt_config?.smart_format === undefined ? 'selected' : ''}>Agent Default</option>
                <option value="true" ${config.stt_config?.smart_format === true ? 'selected' : ''}>Enabled</option>
                <option value="false" ${config.stt_config?.smart_format === false ? 'selected' : ''}>Disabled</option>
              </select>
            </div>
            <div class="form-group">
              <label>
                Endpointing (ms)
                <span class="param-tag static">Static</span>
              </label>
              <input
                type="number"
                id="stt-endpointing"
                value="${config.stt_config?.endpointing || ''}"
                placeholder="Leave empty for agent default (300ms)"
              />
              <p class="field-help">
                Silence duration before considering speech end. 200-250ms = fast, 300-400ms = balanced, 500+ms = patient.
              </p>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button id="cancel-btn" class="btn btn-secondary">Cancel</button>
          <button id="save-btn" class="btn btn-primary">Save Changes</button>
        </div>
      </div>
    `;

    // Setup drag and drop for both lists
    this.setupDragDrop('primary-models', 'P');
    this.setupDragDrop('fallback-models', 'F');

    // Setup event delegation for buttons
    modalBody.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const model = target.getAttribute('data-model');
      if (!model) return;

      if (target.classList.contains('add-model')) {
        const listType = target.getAttribute('data-list') as 'primary' | 'fallback';
        this.addModel(model, listType);
      } else if (target.classList.contains('remove-model')) {
        const listType = target.getAttribute('data-list') as 'primary' | 'fallback';
        this.removeModel(model, listType);
      }
    });

    // Setup form buttons
    document.getElementById('cancel-btn')?.addEventListener('click', () => this.hideModal());
    document.getElementById('save-btn')?.addEventListener('click', () => this.saveTask());

    modal.style.display = 'flex';
  }

  private setupDragDrop(containerId: string, prefix: string): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    let draggedElement: HTMLElement | null = null;

    container.addEventListener('dragstart', (e) => {
      draggedElement = e.target as HTMLElement;
      draggedElement.classList.add('dragging');
    });

    container.addEventListener('dragend', (e) => {
      draggedElement?.classList.remove('dragging');
      draggedElement = null;
    });

    container.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!draggedElement) return;

      const afterElement = this.getDragAfterElement(container, e.clientY);
      if (afterElement == null) {
        container.appendChild(draggedElement);
      } else {
        container.insertBefore(draggedElement, afterElement);
      }
      this.updatePriorityNumbers(containerId, prefix);
    });
  }

  private getDragAfterElement(container: HTMLElement, y: number): HTMLElement | null {
    const draggableElements = [...container.querySelectorAll('.model-item:not(.dragging)')] as HTMLElement[];

    return draggableElements.reduce<{ offset: number; element: HTMLElement | null }>((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child };
      } else {
        return closest;
      }
    }, { offset: Number.NEGATIVE_INFINITY, element: null }).element;
  }

  private addModel(model: string, listType: 'primary' | 'fallback'): void {
    const selectedContainer = document.getElementById(`${listType}-models`);
    const availableContainer = document.getElementById('available-models');
    if (!selectedContainer || !availableContainer) return;

    const prefix = listType === 'primary' ? 'P' : 'F';

    // Add to target container
    const newItem = document.createElement('div');
    newItem.className = `model-item ${listType}`;
    newItem.setAttribute('data-model', model);
    newItem.setAttribute('draggable', 'true');
    newItem.innerHTML = `
      <span class="drag-handle">☰</span>
      <span class="model-priority">${prefix}${selectedContainer.children.length + 1}</span>
      <code>${model}</code>
      <button class="remove-model" data-model="${model}" data-list="${listType}">❌</button>
    `;

    selectedContainer.appendChild(newItem);

    // Remove from available
    const availableItem = availableContainer.querySelector(`[data-model="${model}"]`);
    if (availableItem) availableItem.remove();

    // Re-setup drag and drop is not needed as listeners are on container
    this.updatePriorityNumbers(`${listType}-models`, prefix);

    // Show no results if empty
    if (availableContainer.querySelectorAll('.model-item').length === 0) {
      availableContainer.innerHTML = '<p class="no-results">All models selected</p>';
    }
  }

  private removeModel(model: string, listType: 'primary' | 'fallback'): void {
    const selectedContainer = document.getElementById(`${listType}-models`);
    const availableContainer = document.getElementById('available-models');
    if (!selectedContainer || !availableContainer) return;

    // Remove from selected
    const selectedItem = selectedContainer.querySelector(`[data-model="${model}"]`);
    if (selectedItem) selectedItem.remove();

    // Add back to available
    if (availableContainer.querySelector('.no-results')) {
      availableContainer.innerHTML = '';
    }

    const newItem = document.createElement('div');
    newItem.className = 'model-item available';
    newItem.setAttribute('data-model', model);
    newItem.innerHTML = `
      <code>${model}</code>
      <div class="add-buttons">
        <button class="add-model" data-model="${model}" data-list="primary">➕ Primary</button>
        <button class="add-model" data-model="${model}" data-list="fallback">➕ Fallback</button>
      </div>
    `;

    availableContainer.appendChild(newItem);

    this.updatePriorityNumbers(`${listType}-models`, listType === 'primary' ? 'P' : 'F');
  }

  private updatePriorityNumbers(containerId: string, prefix: string): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    const items = container.querySelectorAll('.model-item');
    items.forEach((item, index) => {
      const prioritySpan = item.querySelector('.model-priority');
      if (prioritySpan) {
        prioritySpan.textContent = `${prefix}${index + 1}`;
      }
    });
  }

  private async saveTask(): Promise<void> {
    if (!this.editingTask) return;

    const primaryContainer = document.getElementById('primary-models');
    const fallbackContainer = document.getElementById('fallback-models');
    const qualityInput = document.getElementById('quality-score') as HTMLInputElement;
    const descInput = document.getElementById('description') as HTMLInputElement;

    if (!primaryContainer || !fallbackContainer || !qualityInput || !descInput) return;

    const primaryModels = Array.from(primaryContainer.querySelectorAll('.model-item'))
      .map(item => item.getAttribute('data-model'))
      .filter((m): m is string => m !== null);

    const fallbackModels = Array.from(fallbackContainer.querySelectorAll('.model-item'))
      .map(item => item.getAttribute('data-model'))
      .filter((m): m is string => m !== null);

    if (primaryModels.length === 0) {
      alert('⚠️ Please select at least one primary model');
      return;
    }

    const sttLanguageInput = document.getElementById('stt-language') as HTMLInputElement;
    const sttSmartFormatInput = document.getElementById('stt-smart-format') as HTMLSelectElement;
    const sttEndpointingInput = document.getElementById('stt-endpointing') as HTMLInputElement;

    // Build STT config - only include non-empty values
    const sttConfig: STTConfig = {};
    if (sttLanguageInput?.value.trim()) sttConfig.language = sttLanguageInput.value.trim();
    if (sttSmartFormatInput?.value) sttConfig.smart_format = sttSmartFormatInput.value === 'true';
    if (sttEndpointingInput?.value) sttConfig.endpointing = parseInt(sttEndpointingInput.value);

    // Warn if language is configured (overrides dynamic user preference)
    if (sttConfig.language) {
      const confirmed = confirm(
        '⚠️ WARNING: Language Override Detected\n\n' +
        `You have configured language: "${sttConfig.language}"\n\n` +
        'This will OVERRIDE the language parameter from the LiveKit agent, ' +
        'which means users will NOT be able to change their language preference ' +
        'dynamically in the iOS app.\n\n' +
        'Recommendation: Leave language empty to allow dynamic switching.\n\n' +
        'Do you want to continue with this override?'
      );
      if (!confirmed) return;
    }

    // Only add stt_config if at least one field is set
    const hasSttConfig = Object.keys(sttConfig).length > 0;

    const config: TaskRoutingConfig = {
      _version: 1,
      primary_models: primaryModels,
      fallback_models: fallbackModels,
      quality_score: parseFloat(qualityInput.value),
      reason: descInput.value,
      stt_config: hasSttConfig ? sttConfig : undefined,
    };

    try {
      await this.apiClient.put('/v1/admin/config/task-routing', {
        taskName: this.editingTask,
        config
      });

      alert('✅ Task routing updated successfully!');
      this.hideModal();
      await this.loadTasks();
    } catch (error) {
      alert(`❌ Failed to save: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  private hideModal(): void {
    const modal = document.getElementById('task-edit-modal');
    if (modal) modal.style.display = 'none';
    this.editingTask = null;
  }

  private async deleteTask(task: string): Promise<void> {
    if (!confirm(`Reset "${task}" to default configuration?\n\nThis will remove the KV override.`)) {
      return;
    }

    try {
      await this.apiClient.delete(`/v1/admin/config/task-routing/${encodeURIComponent(task)}`);
      alert('✅ Task routing reset to default!');
      await this.loadTasks();
    } catch (error) {
      alert(`❌ Failed: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}
